import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from 'react-router-dom';
import { ConfigContext } from "../../contexts/config-context";
import ListingAPI from "../../axios/listing";
import LocationAPI from "../../axios/locations";
import { typesFor, useListingForms } from "../../components/shared-components/listing-forms";
import { HeroSearch } from "./components/hero-search";
import { FeaturedListings } from "./components/featured-listings";
import { formatNumber, LISTING_ROUTES } from "./home-utils";
import "../../hs-theme.css";
import "./main-page.css";

const heroImage = require("../../assets/images/home.jpg");
const img1 = require("../../assets/images/trazis.jpg");
const img2 = require("../../assets/images/saveti.jpg");
const img3 = require("../../assets/images/dokumentacija.jpg");
const img4 = require("../../assets/images/kredit.jpg");

// the six largest cities by the 2022 census
const POPULAR_CITIES = ['Beograd', 'Novi Sad', 'Niš', 'Kragujevac', 'Subotica', 'Leskovac'];
// photo sources: assets/images/cities/CREDITS.md
// position = which part of the photo stays visible when the card crops it
// all of them are free to use without crediting the author (public domain, CC0 or the Unsplash licence)
const CITY_IMAGES = {
  'Beograd': { src: require("../../assets/images/cities/beograd.jpg"), position: '57% center' },
  'Novi Sad': { src: require("../../assets/images/cities/novi-sad.jpg"), position: 'center 65%' },
  'Niš': { src: require("../../assets/images/cities/nis.jpg"), position: 'center' },
  'Kragujevac': { src: require("../../assets/images/cities/kragujevac.jpg"), position: '65% 60%' },
  'Subotica': { src: require("../../assets/images/cities/subotica.jpg"), position: 'center 60%' },
  'Leskovac': { src: require("../../assets/images/cities/leskovac.jpg"), position: 'center 60%' },
};

const GUIDES = [
  { title: 'title_1', text: 'guide_text_1', img: img1, path: '/trazis-nekretninu' },
  { title: 'title_2', text: 'guide_text_2', img: img2, path: '/saveti' },
  { title: 'title_3', text: 'guide_text_3', img: img3, path: '/dokumentacija' },
  { title: 'title_4', text: 'guide_text_4', img: img4, path: '/kredit' },
];

export const MainPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["home"]);
  const { searchData } = useContext(ConfigContext);

  const [tab, setTab] = useState(0);
  const [places, setPlaces] = useState([]);
  const [typeCounts, setTypeCounts] = useState({});
  const [cityCounts, setCityCounts] = useState({});

  const popularPlaces = POPULAR_CITIES
    .map(name => places.find(p => p.label.toLowerCase() === name.toLowerCase()))
    .filter(Boolean);

  useEffect(() => {
    LocationAPI.getListOfPlaces()
      .then(res => setPlaces(res.data))
      .catch(err => console.log(err));
  }, []);

  // the property types listed under the tab's kind of listing; business premises stands for all its kinds
  const { forms, types } = useListingForms(i18n.language);
  const propertyTypes = useMemo(() => typesFor(forms, types, tab + 1), [forms, types, tab]);

  useEffect(() => {
    let cancelled = false;
    Promise.all(propertyTypes.map(type =>
      ListingAPI.getListingsSearchCount({ ...searchData, listing_type: String(tab + 1), property_type: [type.id] })
        .then(res => [type.id, res.data.total_listings])
    )).then(entries => {
      if (!cancelled) setTypeCounts(Object.fromEntries(entries));
    }).catch(err => console.log(err));
    return () => { cancelled = true; };
  }, [propertyTypes]);

  useEffect(() => {
    let cancelled = false;
    Promise.all(popularPlaces.map(place =>
      ListingAPI.getListingsSearchCount({ ...searchData, listing_type: "", place })
        .then(res => [place.id, res.data.total_listings])
    )).then(entries => {
      if (!cancelled) setCityCounts(Object.fromEntries(entries));
    }).catch(err => console.log(err));
    return () => { cancelled = true; };
  }, [places]);

  const openSearch = (overrides) => {
    sessionStorage.removeItem('currentPage');
    navigate(LISTING_ROUTES[tab], {
      state: { data: { ...searchData, listing_type: String(tab + 1), ...overrides } }
    });
  };

  return (
    <div className="hs hs-home">

      <section className="hs-hero-section">
        <div className="hs-hero" style={{ backgroundImage: `url(${heroImage})` }}>
          <div className="hs-hero-overlay"></div>
          <div className="hs-hero-content">
            <p className="hs-hero-eyebrow">{t('hero_eyebrow')}</p>
            <h1>{t('title_main')}</h1>
            <p className="hs-hero-text">{t('hero_text')}</p>
          </div>
        </div>
        <HeroSearch tab={tab} setTab={setTab} places={places} propertyTypes={propertyTypes}
          typeCounts={typeCounts} popularPlaces={popularPlaces} />
      </section>

      {propertyTypes.length > 0 &&
        <section className="hs-section hs-categories-section">
          <div className="hs-categories">
            {propertyTypes.map(type => (
              <button key={`category-${type.id}`} className="hs-category" onClick={() => openSearch({ property_type: [type.id] })}>
                <span className="hs-category-text">
                  <span className="hs-category-label">{type.label}</span>
                  <span className="hs-category-count">{formatNumber(typeCounts[type.id])} {t('listings_count')}</span>
                </span>
                <span className="hs-category-arrow">→</span>
              </button>
            ))}
          </div>
        </section>}

      <FeaturedListings />

      {popularPlaces.length > 0 &&
        <section className="hs-section">
          <div className="hs-section-head">
            <div>
              <p className="hs-eyebrow">{t('cities_eyebrow')}</p>
              <h2 className="hs-h2">{t('cities_title')}</h2>
            </div>
          </div>
          <div className="hs-cities">
            {popularPlaces.map(place => (
              <button key={`city-${place.id}`} className="hs-city"
                style={CITY_IMAGES[place.label] && { backgroundImage: `url(${CITY_IMAGES[place.label].src})`, backgroundPosition: CITY_IMAGES[place.label].position }}
                onClick={() => openSearch({ place })}>
                <span className="hs-city-overlay"></span>
                <span className="hs-city-info">
                  <span>
                    <span className="hs-city-name">{place.label}</span>
                    <span className="hs-city-count">{formatNumber(cityCounts[place.id])} {t('listings_count')}</span>
                  </span>
                  <span className="hs-city-arrow">→</span>
                </span>
              </button>
            ))}
          </div>
        </section>}

      <section className="hs-section">
        <div className="hs-section-head">
          <div>
            <p className="hs-eyebrow">{t('guide_eyebrow')}</p>
            <h2 className="hs-h2 hs-h2-narrow">{t('guide_title')}</h2>
          </div>
        </div>
        <div className="hs-guides">
          {GUIDES.map(guide => (
            <Link key={guide.path} to={guide.path} className="hs-guide">
              <div className="hs-guide-img" style={{ backgroundImage: `url(${guide.img})` }}></div>
              <div className="hs-guide-body">
                <span className="hs-guide-title">{t(guide.title)}</span>
                <span className="hs-guide-text">{t(guide.text)}</span>
                <span className="hs-guide-more">{t('read_more')} →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="hs-section hs-cta-section">
        <div className="hs-cta">
          <svg className="hs-cta-mark" width="360" height="396" viewBox="0 0 40 44" fill="none"><path d="M20 1.5 38 11.75v20.5L20 42.5 2 32.25v-20.5Z" fill="#fff" /></svg>
          <div className="hs-cta-main">
            <p className="hs-cta-eyebrow">{t('cta_eyebrow')}</p>
            <h2>{t('cta_title')}</h2>
            <p className="hs-cta-text">{t('cta_text')}</p>
            <div className="hs-cta-buttons">
              <Link to="/postavi-oglas" className="hs-cta-primary">{t('cta_post')}</Link>
              <Link to="/korisnik/oglasavanje" className="hs-cta-secondary">{t('cta_packages')}</Link>
            </div>
          </div>
          <div className="hs-cta-steps">
            {[1, 2, 3].map(step => (
              <div key={`step-${step}`} className="hs-cta-step">
                <span className="hs-cta-step-num">{step}</span>
                <span>
                  <span className="hs-cta-step-title">{t(`cta_step_${step}_title`)}</span>
                  <span className="hs-cta-step-text">{t(`cta_step_${step}_text`)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};
